<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/mounting_timeslot_registrations/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
    <action
      v-if="variant === 'completed' && isAdmin"
      :icon="SuccessIcon"
      :label="$t('pages.mountings.mounting.mountingRegistrations.approveAllButton')"
      :busy="loadingApproved"
      @click="approveCheckedMountings"
    />
    <action
      v-if="isAdmin && !props.all"
      :icon="AddIcon"
      :label="$t('pages.mountings.mounting.mountingRegistrations.modal.edit.buttonText')"
      @click="
        editedMountingTimeslotRegistration = null;
        mountingTimeslotRegistrationEditModalOpen = true;
      "
    />
  </toolbar>
  <mounting-timeslot-registration-box-modal
    v-model:open="mountingTimeslotRegistrationBoxModalOpen"
    v-model:mounting-timeslot-registration="editedMountingTimeslotRegistration"
    @saved="refetch"
  />
  <mounting-timeslot-registration-modal
    v-model:open="mountingTimeslotRegistrationStatusModalOpen"
    v-model:mounting-timeslot-registration="editedMountingTimeslotRegistration"
    @saved="refetch"
  />
  <mounting-timeslot-registration-manual-modal
    v-model:open="mountingTimeslotRegistrationManualModalOpen"
    v-model:mounting-timeslot-registration="editedMountingTimeslotRegistration"
    @saved="refetch"
  />
  <mounting-timeslot-registration-edit-modal
    v-model:open="mountingTimeslotRegistrationEditModalOpen"
    v-model:mounting-timeslot-registration="editedMountingTimeslotRegistration"
    :mounting-timeslot="props.modelValue"
    @saved="refetch"
  />
  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :rowClick="openModal"
        :visible-columns="visibleColumns"
        :actions="actions"
        :choosable="variant === 'completed'"
        v-model:checked-ids-list="checkedIdsList"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import SuccessIcon from '@images/success.svg?component';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import AddIcon from '@images/panes/add.svg?component';
import Export from '@utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {
  collOptionsProvider,
  enumOptionsProvider,
  useProvider,
  withExtraOptions,
} from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {
  Driver,
  HydraCollection,
  MountingTimeslot,
  MountingTimeslotRegistration,
} from '@/entities/Entities';

import { useNotify } from '@utils/notifications';
import RemoveBoxModal from '@pages/mountings/mounting/RemoveBoxModal.vue';
import MountingTimeslotRegistrationBoxModal from '@pages/mountings/mounting/timeslots/MountingTimeslotRegistrationModal.vue';
import MountingTimeslotRegistrationModal from '@pages/mountings/mounting/MountingTimeslotRegistrationModal.vue';
import MountingTimeslotRegistrationManualModal from '@pages/mountings/mounting/timeslots/MountingTimeslotRegistrationManualModal.vue';
import MountingTimeslotRegistrationEditModal from '@pages/mountings/mounting/timeslots/MountingTimeslotRegistrationEditModal.vue';
import { Option } from '@utils/filter/filterTypes/Dropdown.vue';
import CustomButton from '@utils/CustomButton.vue';
import Action from '@utils/toolbar/Action.vue';
import { useFetchAction } from '@hooks/fetch';
import { useStore } from '@/store';
import { useConfirmPrompt } from '@utils/prompt/injects';
import { useQueryRef } from '@/hooks/url';
import { Region } from '@/entities/Entities';
import { useDelete } from '@/hooks/api';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import { useTableFields } from '@hooks/table-configuration';

interface Props {
  modelValue: MountingTimeslot | null;
  variant: 'pending' | 'completed' | 'approved';
  all?: boolean;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'timeslot.startTime');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const mountingTimeslotRegistrationBoxModalOpen = ref(false);
const editedMountingTimeslotRegistration = ref();
const mountingTimeslotRegistrationEditModalOpen = ref(false);
const mountingTimeslotRegistrationManualModalOpen = ref(false);
const mountingTimeslotRegistrationStatusModalOpen = ref(false);

const { t } = useI18n();

const csvTitle = computed(() => {
  if (props.all) {
    return t(`csv.title.mountingTimeslotRegistrations.${props.variant}`);
  }
  return t(`csv.title.mountingTimeslotRegistrationsForTimeslot.${props.variant}`, {
    timeslot: `${
      props.modelValue?.startTime ? format(new Date(props.modelValue?.startTime), 'dd.MM.yyyy') : ''
    } ${props.modelValue?.mounting?.address ?? ''}`,
  });
});

const [deleteMountingRegistration, { loading: deleting }] = useDelete();

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  ...(props.all ? {} : { timeslot: props.modelValue?.id ?? '' }),
  ...(props.variant === 'pending'
    ? {
        status: 'pending',
        approved: false,
      }
    : {}),
  ...(props.variant === 'completed'
    ? {
        'not[status]': 'pending',
        approved: false,
      }
    : {}),
  ...(props.variant === 'approved'
    ? {
        'not[status]': 'pending',
        approved: true,
      }
    : {}),
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];

const checkedIdsList = ref<string[]>([]);

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const openModal =
  props.variant !== 'approved'
    ? (item: MountingTimeslotRegistration, column?: Column): void => {
        mountingTimeslotRegistrationBoxModalOpen.value = true;
        editedMountingTimeslotRegistration.value = { ...item };
      }
    : undefined;

const actions = [
  ...(props.variant === 'completed'
    ? [
        {
          title: t('pages.mountings.mounting.mountingRegistrations.actions.approve'),
          action: async (item: MountingTimeslotRegistration) => {
            const answer = await confirm?.(
              t('pages.mountings.mounting.mountingRegistrations.confirmApproveOne'),
            );
            if (answer) {
              await approveMountings([item.id]);
            }
          },
          condition: (item: any) => isAdmin,
        },
      ]
    : []),
  {
    title: t('pages.mountings.mounting.mountingRegistrations.actions.manual'),
    action: (item: MountingTimeslotRegistration) => {
      mountingTimeslotRegistrationManualModalOpen.value = true;
      editedMountingTimeslotRegistration.value = { ...item };
    },
  },
  {
    title: t('pages.mountings.mounting.mountingRegistrations.actions.status'),
    action: (item: MountingTimeslotRegistration) => {
      mountingTimeslotRegistrationStatusModalOpen.value = true;
      editedMountingTimeslotRegistration.value = { ...item };
    },
  },
  ...(isAdmin
    ? [
        {
          title: t('pages.mountings.mounting.mountingRegistrations.actions.delete'),
          action: async (item: MountingTimeslot) => {
            if (await deleteMountingRegistration(`/mounting_timeslot_registrations/${item.id}`)) {
              // eslint-disable-next-line no-use-before-define
              refetch();
            }
          },
          condition: (item: any) => isAdmin,
        },
      ]
    : []),
];

const possibleStatus = ref<Option[]>();
enumOptionsProvider('/enums/MountingTimeslotRegistrationStatus', (item) => ({
  label: t(`mountingTimeslotRegistration.statusText.${item}`),
  value: item,
}))().then((val) => {
  possibleStatus.value = val.filter((el) => el.value !== 'pending');
});

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'timeslot.startTime',
    title: t('mountingTimeslotRegistration.startTime'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        label: t('mountingTimeslotRegistration.startTime'),
      },
    },
  },
  {
    key: 'timeslot.duration',
    title: t('mountingTimeslot.duration'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('mountingTimeslot.duration'),
      },
    },
  },
  ...(props.all
    ? [
        {
          key: 'timeslot.mounting.address',
          title: t('mounting.address'),
          group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
          sortable: true,
          visible: true,
          filter: {
            type: 'text',
            group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
            options: {
              label: t('mounting.address'),
            },
          },
        },
      ]
    : []),
  ...(props.all
    ? [
        {
          key: 'timeslot.mounting.license',
          title: t('mounting.license'),
          group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.forBusiness'),
          sortable: true,
          visible: true,
          computed: (mountingTimeslotRegistration: MountingTimeslotRegistration) => {
            const license = mountingTimeslotRegistration?.timeslot?.mounting?.license;
            if (!license) return '-';
            return `${license.company?.name} - ${
              license.startDate ? format(new Date(license.startDate), 'dd.MM.yyyy') : ''
            } - ${license.endDate ? format(new Date(license.endDate), 'dd.MM.yyyy') : ''}`;
          },
          filter: {
            key: 'timeslot.mounting.license',
            type: 'dropdown',
            group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.forBusiness'),
            options: {
              label: t('licenses.license'),
              searchable: true,
              filterResults: false,
              clearOnSelect: true,
              delay: 0,
              resolveOnLoad: true,
              options: (query: string, select: any) => {
                const input = query || select.modelValue;
                const extraOptions = [
                  {
                    label: t('licenses.no'),
                    value: 'none',
                    disabled: false,
                  },
                  {
                    label: t('licenses.any'),
                    value: 'any',
                    disabled: false,
                  },
                ];

                if (!input || input === '') {
                  extraOptions.push({
                    label: t('inputSearchString'),
                    value: '-1',
                    disabled: true,
                  });
                  return Promise.resolve(extraOptions);
                }

                const filterQuery: ProviderParams = {
                  filter: query ? { 'company.name': input } : { id: select.modelValue },
                };
                return withExtraOptions(
                  collOptionsProvider<License>('/licenses', filterQuery, (value) => ({
                    label: `${value.company?.name} (${
                      value.startDate ? format(new Date(value.startDate), 'dd.MM.yyyy') : ''
                    } - ${value.endDate ? format(new Date(value.endDate), 'dd.MM.yyyy') : ''})`,
                    value: value.id,
                  })),
                  extraOptions,
                )();
              },
              valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
                if (value === 'none') {
                  delete m['license'];
                  m['exists[timeslot.mounting.license]'] = false;
                } else if (value === 'any') {
                  delete m['timeslot.mounting.license'];
                  m['exists[timeslot.mounting.license]'] = true;
                } else {
                  delete m['exists[timeslot.mounting.license]'];
                  m[key] = value;
                }
              },
              valueGetter: (m: Record<string, unknown>, key: string) => {
                if ('exists[timeslot.mounting.license]' in m) {
                  return m['exists[timeslot.mounting.license]'] ? 'any' : 'none';
                }
                return m[key];
              },
            },
          },
        },
      ]
    : []),
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    ...(props.variant === 'approved'
      ? {
          to: (mountingTimeslotRegistration: MountingTimeslotRegistration) => ({
            name: 'driver-details',
            params: { driverId: mountingTimeslotRegistration?.driver?.id ?? 'null' },
          }),
        }
      : {}),
    filter: {
      type: 'text',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    ...(props.variant === 'approved'
      ? {
          to: (mountingTimeslotRegistration: MountingTimeslotRegistration) => ({
            name: 'driver-details',
            params: { driverId: mountingTimeslotRegistration?.driver?.id ?? 'null' },
          }),
        }
      : {}),
    filter: {
      type: 'text',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'driver.user.phoneNumber',
    title: t('user.phoneNumber'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        label: t('user.phoneNumber'),
      },
    },
  },
  {
    key: 'driver.liabilityDisclaimerSigned',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driver.liabilityDisclaimerSigned'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => (v ? t('yes') : t('no')),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        label: t('driver.liabilityDisclaimerSigned'),
      },
    },
  },
  {
    key: 'driver.region.name',
    title: t('campaign.region'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: props.all,
    filter: {
      key: 'driver.region',
      type: 'dropdown',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>(
          '/regions',
          {
            sort: {
              sortKey: 'name',
              sortDirection: 'ASC',
            },
          },
          (value) => ({
            label: value.name,
            value: value.id,
          }),
        ),
        label: t('campaign.region'),
      },
    },
  },
  // {
  //   key: 'driver.user.email',
  //   title: t('user.email'),
  //   group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
  //   sortable: true,
  //   visible: true,
  //   filter: {
  //     type: 'text',
  //     group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
  //     options: {
  //       label: t('user.email'),
  //     },
  //   },
  // },
  // {
  //   key: 'driver.user.phoneNumber',
  //   title: t('user.phoneNumber'),
  //   group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
  //   sortable: true,
  //   visible: true,
  //   filter: {
  //     type: 'text',
  //     group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
  //     options: {
  //       label: t('user.phoneNumber'),
  //     },
  //   },
  // },
  {
    key: 'campaignRegistration.campaign.name',
    title: t('mountingTimeslotRegistration.campaign'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        label: t('mountingTimeslotRegistration.campaign'),
      },
    },
  },
  {
    key: 'type',
    title: t('mountingTimeslotRegistration.type.DEFAULT'),
    csvTranslations: {
      mounting: t('mountingTimeslotRegistration.type.mounting'),
      unmounting: t('mountingTimeslotRegistration.type.unmounting'),
      change: t('mountingTimeslotRegistration.type.change'),
      fix: t('mountingTimeslotRegistration.type.fix'),
    },
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`mountingTimeslotRegistration.type.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/MountingTimeslotRegistrationType', (item) => ({
          value: item,
          label: t(`mountingTimeslotRegistration.type.${item}`),
        })),
        label: t('mountingTimeslotRegistration.type.DEFAULT'),
      },
    },
  },
  ...(props.variant !== 'pending'
    ? [
        {
          key: 'status',
          csvTranslations: {
            pending: t(`mountingTimeslotRegistration.statusText.pending`),
            completed: t(`mountingTimeslotRegistration.statusText.completed`),
            canceled: t(`mountingTimeslotRegistration.statusText.canceled`),
            missed: t(`mountingTimeslotRegistration.statusText.missed`),
            other: t(`mountingTimeslotRegistration.statusText.other`),
          },
          title: t('mountingTimeslotRegistration.status'),
          group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
          sortable: true,
          visible: true,
          formatter: (item: string) =>
            item ? t(`mountingTimeslotRegistration.statusText.${item}`) : '',
          filter: {
            type: 'dropdown',
            group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
            options: {
              options: possibleStatus,
              label: t('mountingTimeslotRegistration.status'),
            },
          },
        },
      ]
    : []),
  {
    key: 'driver.box.type',
    csvTranslations: {
      null: t('driver.oldBoxVersion.null'),
      v1: t('driver.oldBoxVersion.v1'),
      v2: t('driver.oldBoxVersion.v2'),
    },
    title: `${t('box.driver')} ${t('box.type')}`,
    group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`driver.oldBoxVersion.${v}`) : t('driver.oldBoxVersion.null')),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxType'), [
          { label: t('driver.oldBoxVersion.null'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['driver.box.type'];
            m['exists[driver.box]'] = false;
          } else {
            delete m['exists[driver.box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[driver.box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: `${t('box.driver')} ${t('box.type')}`,
      },
    },
  },
  {
    key: 'driver.box.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: `${t('box.driver')} ${t('box.variant.DEFAULT')}`,
    group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`box.variant.${v}`) : t('driver.oldBoxVersion.null')),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        options: withExtraOptions(
          enumOptionsProvider('/enums/BoxVariant', (item) => ({
            value: item,
            label: t(`box.variant.${item}`),
          })),
          [{ label: t('driver.oldBoxVersion.null'), value: 'noBox' }],
        ),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['driver.box.variant'];
            m['exists[driver.box]'] = false;
          } else {
            delete m['exists[driver.box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[driver.box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: `${t('box.driver')} ${t('box.variant.DEFAULT')}`,
      },
    },
  },
  {
    key: 'box.type',
    csvTranslations: {
      null: t('driver.oldBoxVersion.null'),
      v1: t('driver.oldBoxVersion.v1'),
      v2: t('driver.oldBoxVersion.v2'),
    },
    title: `${t('box.new')} ${t('box.type')}`,
    group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`driver.oldBoxVersion.${v}`) : t('driver.oldBoxVersion.null')),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxType'), [
          { label: t('driver.oldBoxVersion.null'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['box.type'];
            m['exists[box]'] = false;
          } else {
            delete m['exists[box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: `${t('box.new')} ${t('box.type')}`,
      },
    },
  },
  {
    key: 'box.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: `${t('box.new')} ${t('box.variant.DEFAULT')}`,
    group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`box.variant.${v}`) : t('driver.oldBoxVersion.null')),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        options: withExtraOptions(
          enumOptionsProvider('/enums/BoxVariant', (item) => ({
            value: item,
            label: t(`box.variant.${item}`),
          })),
          [{ label: t('driver.oldBoxVersion.null'), value: 'noBox' }],
        ),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['box.variant'];
            m['exists[box]'] = false;
          } else {
            delete m['exists[box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: `${t('box.new')} ${t('box.variant.DEFAULT')}`,
      },
    },
  },
  {
    key: 'oldBox.type',
    csvTranslations: {
      null: t('driver.oldBoxVersion.null'),
      v1: t('driver.oldBoxVersion.v1'),
      v2: t('driver.oldBoxVersion.v2'),
    },
    title: `${t('box.old')} ${t('box.type')}`,
    group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`driver.oldBoxVersion.${v}`) : t('driver.oldBoxVersion.null')),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxType'), [
          { label: t('driver.oldBoxVersion.null'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['oldBox.type'];
            m['exists[oldBox]'] = false;
          } else {
            delete m['exists[oldBox]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[oldBox]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: `${t('box.old')} ${t('box.type')}`,
      },
    },
  },
  {
    key: 'oldBox.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: `${t('box.old')} ${t('box.variant.DEFAULT')}`,
    group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? t(`box.variant.${v}`) : t('driver.oldBoxVersion.null')),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        options: withExtraOptions(
          enumOptionsProvider('/enums/BoxVariant', (item) => ({
            value: item,
            label: t(`box.variant.${item}`),
          })),
          [{ label: t('driver.oldBoxVersion.null'), value: 'noBox' }],
        ),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['oldBox.variant'];
            m['exists[oldBox]'] = false;
          } else {
            delete m['exists[oldBox]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[oldBox]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: `${t('box.old')} ${t('box.variant.DEFAULT')}`,
      },
    },
  },
  ...(props.variant !== 'pending'
    ? [
        {
          key: 'box.sealNumber',
          title: `${t('box.new')} ${t('box.seal')}`,
          group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
          sortable: true,
          visible: true,
          filter: {
            type: 'text',
            group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
            options: {
              label: `${t('box.new')} ${t('box.seal')}`,
            },
          },
        },
        {
          key: 'box.keyNumber',
          title: `${t('box.new')} ${t('box.keyNumber')}`,
          group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
          sortable: true,
          visible: true,
          filter: {
            type: 'text',
            group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
            options: {
              label: `${t('box.new')} ${t('box.keyNumber')}`,
            },
          },
        },
        {
          key: 'box.stable',
          title: `${t('box.new')} ${t('box.stable')}`,
          group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
          sortable: true,
          visible: true,
          formatter: (v: boolean | null) => (v ? t('yes') : v === null ? t('unknown') : t('no')),
          filter: {
            type: 'dropdown',
            group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
            options: {
              options: [
                {
                  value: 'true',
                  label: t('yes'),
                },
                {
                  value: 'false',
                  label: t('no'),
                },
                {
                  value: 'null',
                  label: t('unknown'),
                },
              ],
              valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
                if (value === 'null') {
                  delete m['box.stable'];
                  m['exists[box.stable]'] = false;
                } else {
                  delete m['exists[box.stable]'];
                  m[key] = value;
                }
              },
              valueGetter: (m: Record<string, unknown>, key: string) => {
                if ('exists[box.stable]' in m) {
                  return 'null';
                }
                return m[key];
              },
              label: `${t('box.new')} ${t('box.stable')}`,
            },
          },
        },
      ]
    : []),
  {
    key: 'driver.bicycleType',
    title: t('driver.bicycleType'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
    formatter: (v: string) => (v ? t(`driver.bicycleTypes.${v}`) : t('driver.bicycleTypes.null')),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Bicycle', (v) => ({
          value: v,
          label: t(`driver.bicycleTypes.${v}`),
        })),
        label: t('driver.bicycleType'),
      },
    },
  },
  {
    key: 'comment',
    title: t('mountingTimeslotRegistration.comment'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: false,
    visible: true,
  },
  {
    key: 'adminComment',
    title: t('mountingTimeslotRegistration.adminComment'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: false,
    visible: true,
  },
  {
    key: 'phoneModel',
    title: t('mountingTimeslotRegistration.phoneModel'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        label: t('mountingTimeslotRegistration.phoneModel'),
      },
    },
  },
  {
    key: 'returnType',
    csvTranslations: {
      v1: t('mountingTimeslotRegistration.returnType.v1'),
      v2_with_key: t('mountingTimeslotRegistration.returnType.v2_with_key'),
      v2_no_key: t('mountingTimeslotRegistration.returnType.v2_no_key'),
      null: '-',
    },
    title: t('mountingTimeslotRegistration.returnType.DEFAULT'),
    group: t('pages.mountings.mounting.mountingRegistrations.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string | null) => (v ? t(`mountingTimeslotRegistration.returnType.${v}`) : '-'),
    filter: {
      type: 'dropdown',
      group: t('pages.mountings.mounting.mountingRegistrations.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/MountingTimeslotRegistrationReturnType', (v) => ({
          value: v,
          label: t(`mountingTimeslotRegistration.returnType.${v}`),
        })),
        label: t('mountingTimeslotRegistration.returnType.DEFAULT'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const blockRequest = computed(() => !props.modelValue && !props.all);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<MountingTimeslotRegistration>>(
  '/mounting_timeslot_registrations',
  providerParams,
  {
    disabled: blockRequest,
  },
);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  }),
);

const notify = useNotify();

const loading = computed(() => loadingTable.value || deleting.value);

const [exec, { loading: loadingApproved }] = useFetchAction();

const confirm = useConfirmPrompt();

const approveCheckedMountings = async () => {
  if (checkedIdsList.value.length > 0) {
    const answer = await confirm?.(
      t('pages.mountings.mounting.mountingRegistrations.confirmApprove'),
    );
    if (answer) {
      await approveMountings(checkedIdsList.value);
    }
  }
};

const approveMountings = async (idList: string[]) => {
  if (isAdmin) {
    try {
      await exec('/mounting_timeslot_registrations/approve', {
        method: 'PUT',
        body: {
          mountingTimeslotRegistrationIds: idList,
        },
      });

      notify?.({
        variant: 'success',
        title: t('pages.mountings.mounting.mountingRegistrations.status.saveSuccess'),
      });

      await refetch();
    } catch (e) {
      notify?.({
        variant: 'error',
        title: t('pages.mountings.mounting.mountingRegistrations.status.saveError'),
      });
    }
  }
};
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
